import {DOM, IDocumentFragment, IDocumentNs} from "lib/commons/xml/dom";
import {IJmlSet, JML} from "lib/commons/xml/jml";
import {IXAddr, IXAddrRange, XA} from "lib/commons/xml/xAddr";
import {HouseOt, isMsgUpdater, ListMsgOt, MsgOt, OHouseOtConfig, OtId} from "lib/edit/ot/houseOt";
import {FactoryRegistry, House, InitSlaveRep, IPublicDoor, Msg, ResetStatesMsg, State} from "lib/edit/ot/urban";


/**
 *
 */
export interface OXmlgHouseConfig extends OHouseOtConfig {
	/** document XML à éditer (en JML) */
	initialJml?: IJmlSet;

	/**
	 * Document XML à éditer (en DOM).
	 * Peut-être un DocumentFragment pour gérer N noeuds racines.
	 * Mais, ATTENTION, chaque Document ne devrait être utilisé que par une seule house
	 * (des props peuvent être accrochés sur Node.ownwerDocument, cf IDocumentInWspHouse par exemple).
	 */
	initialDoc?: Document | DocumentFragment;

	/** configuration "free" du XmlBodyState de cette house. */
	xmlBodyState?: XmlBodyState;

	/** Namespaces XML à ajouter aux exports. */
	namepspaces?: Dict<string>;
}

/**
 * House XML.
 *
 * Limites de l'implémentation HouseOt et XmlHouse :
 * - move non primaire, mais simple del+ins.
 * - manque inclusive bounds dans ins/del pour fragment = set de séquences, en absorbant les ins concurrents entre les séquences.
 * - undo/redo exige un état synchrone pour s'exécuter.
 */
export class XmlHouse extends HouseOt {

	protected _body: XmlBodyState;
	protected _states: Array<State> = [];

	constructor(options: OXmlgHouseConfig) {super(options)}

	protected initHouse(options: OXmlgHouseConfig) {
		super.initHouse(options);
		this._states = [];
		this.putState(options.xmlBodyState || new XmlBodyState("body"));
		if (options.namepspaces) this._body.namespaces = options.namepspaces;
		if (options.initialDoc) {
			this._body.resetDom(options.initialDoc);
		} else if (options.initialJml) {
			this._body.resetJml(options.initialJml);
		}
	}

	get root(): Node {return this._body.root}

	get document(): Document {return this._body.document}

	get namespaces(): Dict<string> {return this._body.namespaces}

	newBatch(): XmlBatch {return new XmlBatch()}

	executeBatch(batch: XmlBatch, fromDoor?: IPublicDoor) {
		if (batch.getState() === 'building') {
			if (batch.msgs.length === 1 && !batch.selAfter && !batch.selBefore && !batch.metas) {
				(fromDoor || this.publicDoor).receiveMsg(batch.msgs[0]);
			} else {
				(fromDoor || this.publicDoor).receiveMsg(batch);
			}
		}
		batch.setDone();
	}


	getStateById<T extends State>(id: string): T {
		if (id === "body") return this._body as unknown as T;
		for (let i = 0, e = this._states.length; i < e; i++) if (this._states[i].id === id) return this._states[i] as T;
		return null;
	}

	/**
	 * Enregistre un state dans la house pour être synchronisé avec les autres houses.
	 *
	 * Attention: si la house est déjà connectée, aucune gestion de dispatch de Msg à ce niveau.
	 * Un envoi explicite de Msg via la publicDoor doit être effectué pour informer de ce nouveau State.
	 *
	 * @param {State} state State qui DOIT posseder un id unique parmi toutes le houses synchronisées.
	 *
	 * TODO mécanisme de suppr des states d'une house slave (sel courante du user...) par le masterRoot lorsque la house slave est déconnectée.
	 */
	putState(state: State) {
		if (state.id === "body") {
			this._body = (state as XmlBodyState).attachHouse(this);
		} else {
			this._states.push(state.attachHouse(this));
		}
	}

	/**
	 * Suppression d'un state dans la house locale.
	 * TODO mécanisme de dispatch de suppr dans les slaves / master.
	 * @param {State} state
	 */
	removeState(state: State) {
		//if (state === this._body) this._body = null; pas possible de supprimer le state body, nécessaire en interne.
		for (let i = 0, e = this._states.length; i < e; i++) if (state === this._states[i]) this._states.splice(i, 1);
	}

	exportFragment(range: IXAddrRange): DocumentFragment {
		return this._body.exportFragment(range)
	}

	exportRange(range: IXAddrRange, format: string, formatOptions?: Jsonisable): Jsonisable {
		return this.exportFromFragment(this._body.exportFragment(range), format, formatOptions);
	}

	exportNodes(xaNodes: IXAddr[], filters: Jsonisable[], format: string, formatOptions?: Jsonisable): Jsonisable {
		const frag = this.document.createDocumentFragment();
		for (let i = 0; i < xaNodes.length; i++) {
			const res = this._body.exportNodeCustom(xaNodes[i], filters[i]);
			if (res) frag.appendChild(res);
		}
		return this.exportFromFragment(frag, format, formatOptions);
	}

	exportFromFragment(frag: IDocumentFragment, format: string, options?: Jsonisable): Jsonisable {
		if (format === 'application/xml') {
			const fragment = frag.ownerDocument.createElementNS(options?.rootTagNs || DOM.SCCORE_NS, options?.rootTagName || DOM.SCFRAGMENT_TAG);
			if (frag.xmlSpacePreserve) fragment.setAttributeNS(DOM.XML_NS, "xml:space", "preserve");
			const ns = this._body.namespaces;
			if (ns) for (const prefix in ns) {
				if (prefix === '') {
					fragment.setAttributeNS(DOM.XMLNS_NS, 'xmlns', ns[prefix]);
				} else {
					fragment.setAttributeNS(DOM.XMLNS_NS, 'xmlns:' + prefix, ns[prefix]);
				}
			}
			fragment.appendChild(frag);
			//console.log("before pullupNs:::", DOM.ser(fragment));
			DOM.pullupNs(fragment);
			return DOM.ser(fragment);
		} else if (format === 'jml') {
			return JML.dom2jml(frag)
		}
		throw Error("Format not found");
	}

	findNodeByXpath(xpath: string): Node {
		const exp = this._body.document.createExpression(xpath, this._body);
		const result = exp.evaluate(this._body.document, XPathResult.FIRST_ORDERED_NODE_TYPE, null);
		return result.singleNodeValue;
	}

	findNodeByXa(xa: IXAddr): Node {
		return XA.findDomLast(xa, this._body.document);
	}

	xApplyMsgOtLocally(msg: MsgOt, correctable: boolean): MsgOt {
		try {
			if (this._body) this._body.update(msg);
			for (let i = 0, e = this._states.length; i < e; i++) this._states[i].update(msg);
		} catch (e) {
			console.error("xApplyMsgOtLocally in house failed", this, msg, e);
			throw e;
		}
		return msg;
	}

	xApplyResetStatesLocally(msg: InitSlaveRep | ResetStatesMsg) {
		super.xApplyResetStatesLocally(msg);
		if (this._body) this._body.update(msg);
		this._states = msg.datas.states.map((st: any) => HouseOt.STATE_FACTORYREG.newFromJson(st).attachHouse(this));
	}

	xInitDom(root: Node) {}

	writeFullStates(datas: IXmlResetInitDatas, options?: any) {
		datas.body = JML.dom2jml(this._body.root);
		datas.states = this._states.map(st => st.asJson());
	}

	killHouse() {
		super.killHouse();
		this._body.attachHouse(null);
		this._states.forEach((state) => {state.attachHouse(null)})
	}
}

/** Type de ResetStatesMsg.datas et InitSlaveRep.datas pour une house de type xml. */
export interface IXmlResetInitDatas {
	progress: number
	body?: IJmlSet
	schema?: Jsonisable
	states?: Jsonisable[]
}

/**
 * Mémoire du contenu stocké dans la house sous forme de DOM.
 */
export class XmlBodyState extends State {
	static readonly type = "xmlBody";

	house: XmlHouse;
	root: Node;

	get document(): IDocumentNs {return this.root instanceof Document ? this.root : this.root.ownerDocument}

	protected _namespaces: Dict<string>;

	get namespaces(): Dict<string> {return this._namespaces}

	set namespaces(v: Dict<string>) {
		this._namespaces = v;
		if (this.root) this.document.namespaces = v;
	}

	constructor(id: string) {
		super();
		this.id = id;
	}

	resetJml(jml: IJmlSet): this {
		const doc = DOM.newDomDoc() as IDocumentNs;
		if (this._namespaces) doc.namespaces = this._namespaces;
		this.setRoot(JML.jmlToDom(jml, doc));
		if (XmlHouse.DEBUG && this.house) console.log("resetJml::\n" + this.toString());
		return this;
	}

	resetDom(root: Node): this {
		while (root.parentNode) root = root.parentNode; //pour être en phase avec XA.from(node) qui remonte jusqu'à la racine.
		if (this._namespaces) ((root.ownerDocument || root) as IDocumentNs).namespaces = this._namespaces;
		this.setRoot(root);
		if (XmlHouse.DEBUG && this.house) console.log("resetDom::\n" + this.toString());
		return this;
	}

	protected setRoot(root: Node) {
		this.root = root;
		this.house?.xInitDom(root);
	}

	update(msg: Msg) {
		switch (msg.type) {
		case XmlInsertMsg.type : {
			const m = msg as XmlInsertMsg;
			if (m.killed) return;
			if (XmlHouse.ASSERT) if (this.house) m.assertMsgValidity(this.house);
			if (m.xa.length === 1 && this.root instanceof Document && JML.isElt(m.jml[0]) && this.root.documentElement) {
				//Insertion à la racine du document : contrainte XML un seul élément. Donc suppr documentElement avant
				//Peut arriver en cas de correction auto de l'élément racine (ex: meta des res sfm:jpg_jpeg -> sfm:png_gif_jpg_jpeg et absence de migration)
				this.root.documentElement.remove();
			}
			JML.insertJmlInDom(m.xa, m.jml, this.root, true);
			break;
		}
		case XmlDeleteMsg.type : {
			const m = msg as XmlDeleteMsg;
			if (m.killed) return;
			if (XmlHouse.ASSERT) if (this.house) m.assertMsgValidity(this.house);
			//PERF if (msg.jml == null) quand stabilisé on pourra recalculer le jml que si nullifié suite à un adjust.
			m.setJml(JML.extractJmlOrTextFromDom(m.xa, m.len, this.root));  //recalcul du jml supprimé (après adjust)
			DOM.deleteSequenceInDom(m.xa, m.len, this.root);
			break;
		}
		case XmlStrMsg.type : {
			const m = msg as XmlStrMsg;
			if (m.killed) return;
			if (XmlHouse.ASSERT) if (this.house) m.assertMsgValidity(this.house);
			const last = XA.last(m.xa);
			if (typeof last === 'string') {
				const elt = XA.findDomContainer(m.xa, this.root);
				if (!(elt instanceof Element)) throw Error(`Container xAddr part is not an element: ${m.xa}`);
				m.setOld(elt.getAttribute(last));
				const sep = last.indexOf(':');
				const ns = sep > 0 ? DOM.lookupNamespaceURI(elt, last.substring(0, sep)) : null;
				if (m.val == null) {
					if (ns) elt.removeAttributeNS(ns, last.substring(sep + 1));
					else elt.removeAttribute(last);
				} else {
					if (ns) elt.setAttributeNS(ns, last, m.val);
					else elt.setAttribute(last, m.val);
				}
			} else {
				const node = XA.findDomLast(m.xa, this.root);
				m.setOld(node.nodeValue);
				node.nodeValue = m.val;
			}
			break;
		}
		case ResetStatesMsg.type :
		case InitSlaveRep.type : {
			const datas = (msg as ResetStatesMsg | InitSlaveRep).datas as IXmlResetInitDatas;
			this.resetJml(datas.body);
			break;
		}
		default:
			if (isMsgUpdater(msg)) msg.applyUpdates(this);
		}
		if (XmlHouse.DEBUG) console.log(this.toString());
	}

	exportFragment(range: IXAddrRange): DocumentFragment {
		const frag = this.document.createDocumentFragment();
		XA.cloneFragment(this.root, frag, range);
		return frag;
	}

	/** Exporte un noeud avec des filtres (dépendant du schéma). Impl de base : simple clone du noeud. */
	exportNodeCustom(xa: IXAddr, filters?: Jsonisable): Node {
		const node = XA.findDomLast(xa, this.root);
		return node ? node.cloneNode(true) : null;
	}

	/** API XPathResolver (utilisé par Document.createExpression() de l'API DOM). */
	lookupNamespaceURI(prefix: string): string {
		if (prefix === "xml") return "http://www.w3.org/XML/1998/namespace";
		return this._namespaces ? this._namespaces[prefix] : null;
	}

	toJson(json: any) {
		super.toJson(json);
		json.jml = JML.dom2jml(this.root);
	}

	fromJson(json: any, factoryReg: FactoryRegistry<State>) {
		super.fromJson(json, factoryReg);
		this.root = JML.jmlToDom(json.jml);
	}

	toString() {
		return "Body " + this.house + " :: " + DOM.debug(this.root);
	}

}

House.STATE_FACTORYREG.register(XmlBodyState);


/**
 * Mémoire d'un range stockable dans la house. Les metas permettent de qualifier ce XmlRangeState
 * (type, propriétaire, contenu d'une annotation associée...)
 */
export class XmlRangeState extends State implements IXAddrRange {
	static readonly type = "xmlRg";

	start: IXAddr;
	end: IXAddr;

	init(id: string, metas: any, xaStart: IXAddr, xaEnd: IXAddr) {
		this.id = id;
		if (metas) this.metas = metas;
		this.start = XA.freeze(xaStart);
		this.end = XA.freeze(xaEnd);
		return this;
	}

	get isCollapsed(): boolean {return XA.isEquals(this.start, this.end)}

	/** Pour changer le range. */
	setRange(start: IXAddr | null, end?: IXAddr | null, metas?: any) {
		if (start) this.start = XA.freeze(start);
		if (end) this.end = XA.freeze(end);
		if (metas) this.metas = metas;
	}

	update(msg: Msg) {
		switch (msg.type) {
		case XmlInsertMsg.type : {
			const m = msg as XmlInsertMsg;
			if (m.killed) return;
			this.start = XA.freeze(XA.translateInsSeq(this.start, m.xa, JML.lengthJmlOrText(m.jml)));
			this.end = XA.freeze(XA.translateInsSeq(this.end, m.xa, JML.lengthJmlOrText(m.jml)));
			break;
		}
		case XmlDeleteMsg.type : {
			const m = msg as XmlDeleteMsg;
			if (m.killed) return;
			this.start = XA.freeze(XA.translateDelSeq(this.start, m.xa, m.len));
			this.end = XA.freeze(XA.translateDelSeq(this.end, m.xa, m.len));
			break;
		}
		case XmlStrMsg.type : {
			const m = msg as XmlStrMsg;
			if (m.killed) return;
			//TODO si suppr et range pointe l'attr, remonter à l'elt en collapsed ?
			//TODO si reset de la valeur et range dans l'attr, ajuster aux bornes.
			break;
		}
		case ResetStatesMsg.type :
		case InitSlaveRep.type :
			break;
		default:
			if (isMsgUpdater(msg)) msg.applyUpdates(this);
		}
	}

	toJson(json: any) {
		super.toJson(json);
		json.start = this.start;
		json.end = this.end;
	}

	fromJson(json: any, factoryReg: FactoryRegistry<State>) {
		super.fromJson(json, factoryReg);
		this.start = json.start;
		this.end = json.end;
	}

}

House.STATE_FACTORYREG.register(XmlRangeState);


/**
 * Mémoire d'un point stockable dans la house. Les metas permettent de qualifier ce XmlPointState
 * (type, propriétaire, contenu d'une annotation associée...)
 */
export class XmlAddrState extends State {
	static readonly type = "xmlAddr";

	xAddr: IXAddr;

	init(id: string, metas: any, xAddr: IXAddr) {
		if (id) this.id = id;
		if (metas) this.metas = metas;
		this.xAddr = XA.freeze(xAddr);
		return this;
	}

	/** Pour changer ce point. */
	setAddr(xAddr: IXAddr | null, metas?: any) {
		if (xAddr) this.xAddr = XA.freeze(xAddr);
		if (metas) this.metas = metas;
	}

	update(msg: Msg) {
		switch (msg.type) {
		case XmlInsertMsg.type : {
			const m = msg as XmlInsertMsg;
			if (m.killed) return;
			this.xAddr = XA.freeze(XA.translateInsSeq(this.xAddr, m.xa, JML.lengthJmlOrText(m.jml)));
			break;
		}
		case XmlDeleteMsg.type : {
			const m = msg as XmlDeleteMsg;
			if (m.killed) return;
			this.xAddr = XA.freeze(XA.translateDelSeq(this.xAddr, m.xa, m.len));
			break;
		}
		case XmlStrMsg.type : {
			const m = msg as XmlStrMsg;
			if (m.killed) return;
			break;
		}
		case ResetStatesMsg.type :
		case InitSlaveRep.type :
			break;
		default:
			if (isMsgUpdater(msg)) msg.applyUpdates(this);
		}
	}

	toJson(json: any) {
		super.toJson(json);
		json.xAddr = this.xAddr;
	}

	fromJson(json: any, factoryReg: FactoryRegistry<State>) {
		super.fromJson(json, factoryReg);
		this.xAddr = json.xAddr;
	}

}

House.STATE_FACTORYREG.register(XmlAddrState);

/**
 * Message relatif au body Xml de la house.
 */
export interface IXmlMsg extends MsgOt {
	//point de départ de l'action.
	xa: IXAddr;
}

export function isXmlMsg(msg: any): msg is IXmlMsg {
	return "xa" in msg;
}

/**
 * Interface interne pour tous les messages isProgressSensitive de cet XmlHouse.
 * Contient toutes les opérations qu'un message est susceptible de subir
 * comme changement dû à d'autres messages concurents.
 */
export interface IXmlPrgSensitiveMsg extends MsgOt {
	/**
	 * Le msg courant se placera après dans la pile d'historique définitif, une insertion par le msg courant
	 * au même offset que le déplacement spécifié en paramètre se placera après.
	 */
	adjustInsertX(xa: IXAddr, len: number): void;

	/**
	 * Pour undo/redo. Le msg courant issu de l'inversion du msg à annuler ou refaire traverse une insertion pour atteindre
	 * la tête de la pile des msg suivants avant de pouvoir être exécuté/diffusé au master.
	 * @param {string} invertOfOtId Id du MsgOt dont ce message est l'inversion (undo ou redo).
	 */
	crossInsertX(xa: IXAddr, len: number, invertOfOtId: OtId): void;

	/**
	 * Le msg courant est reculé dans son propre historique. S'il s'agit d'une insertion, il doit mémoriser dans une pile
	 * si il doit se placer après ce déplacement spécifié en paramètre lorsqu'il sera rejoué (forward).
	 */
	rewindInsertX(xa: IXAddr, len: number): void;

	/**
	 * Le msg courant est réavancé, S'il s'agit d'une insertion, il doit retrouver l'information dans sa pile pour savoir
	 * si il doit être placé avant ou après ce déplacement spécifié en paramètre.
	 */
	forwardInsertX(xa: IXAddr, len: number): void;

	/**
	 * Le msg courant se placera après dans la pile d'historique définitif, une insertion par le msg courant
	 * au même offset que le déplacement spécifié en paramètre se placera après.
	 * @param dLen Toujours <0
	 */
	adjustDeleteX(xa: IXAddr, dLen: number): void;

	/**
	 * Pour undo/redo. Le msg courant issu de l'inversion du msg à annuler ou refaire traverse une suppression pour atteindre
	 * la tête de la pile des msg suivants avant de pouvoir être exécuté/diffusé au master.
	 * @param dLen Toujours <0
	 */
	crossDeleteX(xa: IXAddr, dLen: number, fromOtId: OtId): void;

	/**
	 * Le msg courant est reculé dans son propre historique. S'il s'agit d'une insertion, il doit mémoriser dans une pile
	 * si il doit se placer après ce déplacement spécifié en paramètre lorsqu'il sera rejoué (forward).
	 * @param dLen Toujours <0
	 */
	rewindDeleteX(xa: IXAddr, dLen: number): void;

	/**
	 * Le msg courant est réavancé, S'il s'agit d'une insertion, il doit retrouver l'information dans sa pile pour savoir
	 * si il doit être placé avant ou après ce déplacement spécifié en paramètre.
	 * @param dLen Toujours <0
	 */
	forwardDeleteX(xa: IXAddr, dLen: number): void;
}

/**
 * Insertion d'un fragment XML ou texte.
 * {
 * xa: [1, 4],
 * jml: [...] ou "..."
 * mem: [] mémoire à chaque rewind.
 * }
 */
export class XmlInsertMsg extends MsgOt implements IXmlPrgSensitiveMsg, IXmlMsg {
	static readonly type = "xmlIns";

	xa: IXAddr;
	jml: IJmlSet | string | null /*msg killed*/;

	protected mem: Array<any>;
	protected _mem: Dict<any>;

	init(xa: IXAddr, jmlOrText: IJmlSet | string | null): this {
		this.xa = XA.freeze(xa);
		if (XmlHouse.ASSERT) if (typeof XA.last(xa) === 'string') throw Error(`Can not use XmlInsertMsg for setting attribute, use XmlStrMsg instead : ${this.xa}'`);
		if (typeof jmlOrText === "string") this.jml = JML.filterForbiddenXmlChars(jmlOrText);
		else this.jml = JML.filterForbiddenXmlCharsInJml(jmlOrText) as IJmlSet;
		return this;
	}

	get isUndoable() {return true}

	get isBodyMutator() {return true}

	get killed() {return this.jml == null}

	kill() {
		this.jml = null;
	}

	get len() {return JML.lengthJmlOrText(this.jml)}

	get end() {return (XA.last(this.xa) as number) + this.len}

	adjust(msg: IXmlPrgSensitiveMsg) {
		msg.adjustInsertX(this.xa, this.len);//TODO transformer api et passer this + hook en param pour msg de type List.
	}

	rewind(msg: IXmlPrgSensitiveMsg) {
		msg.rewindInsertX(this.xa, this.len);
	}

	forward(msg: IXmlPrgSensitiveMsg) {
		msg.forwardInsertX(this.xa, this.len);
	}

	cross(msg: IXmlPrgSensitiveMsg, invertOfOtId: OtId) {
		msg.crossInsertX(this.xa, this.len, invertOfOtId);
	}

	get isMergeable(): boolean {return typeof this.jml === 'string'}

	mergeWith(nextMsg: MsgOt): MsgOt {
		if (nextMsg instanceof XmlInsertMsg) {
			if (typeof this.jml === 'string' && typeof nextMsg.jml === 'string') {
				const offset = XA.last(this.xa);
				if (typeof offset === 'number' && XA.isInSameSeq(this.xa, nextMsg.xa) && XA.last(nextMsg.xa) === offset + this.len) {
					this.jml += nextMsg.jml;
					return this;
				}
			}
		} else if (nextMsg instanceof XmlDeleteMsg) {
			if (typeof this.jml === 'string') {
				const offset = XA.last(this.xa);
				const nextOffset = XA.last(nextMsg.xa);
				if (typeof offset === 'number' && typeof nextOffset === 'number') {
					if (XA.isInSameSeq(this.xa, nextMsg.xa) && nextOffset + nextMsg.len === offset + nextMsg.len) {
						const newLen = this.len - nextMsg.len;
						if (newLen >= 0) {
							this.jml = this.jml.substring(0, newLen);
							return this;
						}
					}
				}
			}
		}
		return null;
	}

	adjustDeleteX(xa: IXAddr, dLen: number) {
		if (dLen === 0) return;
		const myDepth = this.xa.length - 1;
		const depthDel = xa.length - 1;
		for (let depth = 0, max = Math.min(depthDel, myDepth); depth <= max; depth++) {
			const offset = xa[depth];
			const myOffs = this.xa[depth];
			if (typeof offset === 'string' || typeof myOffs === 'string') {
				if (offset === myOffs) {
					// on entre dans le même attribut
					if (depth === depthDel) {
						//l'attr lui-même est supprimé
						this.xa = XA.freeze(xa);	// => On kill l'insert.
						this.kill();
						return;
					}
					continue;
				}
				return; //2 attributs != ou attr et child, pas d'interférence
			}
			if (offset > myOffs) return; 		//Le delete est après notre insertion, pas d'interférence.
			if (depth === depthDel) {
				//On est à la profondeur du delete
				if (offset - dLen <= myOffs) {	//Le delete est intégralement avant notre insertion, on décale
					this.xa = XA.newBd(this.xa).setAtDepth(depth, myOffs + dLen).freeze();
				} else if (depth === myDepth) {	//Le delete et notre insert sont dans la même séquence
					if (offset < myOffs) {
						if (myOffs > offset - dLen) {
							//Notre insertion est après la suppr, on décale
							this.xa = XA.newBd(this.xa).setAtDepth(depth, myOffs + dLen).freeze();
						} else {
							//Notre insertion est dans la suppression, on se replace sur le xa de la suppr
							//et on préseve l'insertion (choix arbitraire != du cas suivant ou l'insertion est dans un sous-noeud)
							this.xa = XA.freeze(xa);
						}
					}
				} else {						//L'insert est un sous-noeud dans le delete
					this.xa = XA.freeze(xa);	// => On kill l'insert.
					this.kill();
				}
			} else {
				//On est encore dans un container du delete
				if (offset < myOffs) return;	//Le delete concerne une autre séquence que notre insert, pas d'interférence.
			}
		}
	}

	crossDeleteX(xa: IXAddr, dLen: number, fromOtId: OtId) {
		if (!this._mem) this._mem = Object.create(null);
		this._mem[fromOtId] = this._jumpDeleteX(xa, -dLen, this._mem[fromOtId]);
	}

	rewindDeleteX(xa: IXAddr, dLen: number) {
		this.adjustInsertX(xa, -dLen);
	}

	forwardDeleteX(xa: IXAddr, dLen: number) {
		this.adjustDeleteX(xa, dLen);
	}

	adjustInsertX(xa: IXAddr, len: number, biasBefore?: boolean) {
		if (len === 0) return;
		const myDepth = this.xa.length - 1;
		const depthDel = xa.length - 1;
		for (let depth = 0, max = Math.min(depthDel, myDepth); depth <= max; depth++) {
			const offset = xa[depth];
			const myOffs = this.xa[depth];
			if (typeof offset === 'string' || typeof myOffs === 'string') {
				if (offset === myOffs) continue; // on entre dans le même attribut
				return; //2 attributs != ou attr et child, pas d'interférence
			}
			if (offset > myOffs) return; 		//L'insert est après notre insertion, pas d'interférence.
			if (depth === depthDel) {
				//On est à la profondeur de l'insert
				if (biasBefore ? offset < myOffs : offset <= myOffs) {			//L'insert est avant notre insertion, on décale
					this.xa = XA.newBd(this.xa).setAtDepth(depth, myOffs + len).freeze();
				}
			} else {
				//On est encore dans un container de l'insert
				if (offset < myOffs) return;	//L'insert concerne une autre séquence que notre insert, pas d'interférence.
			}
		}
	}

	crossInsertX(xa: IXAddr, len: number, invertOfOtId: OtId) {
		//(~= rewindDelete pour la mémoire + forwardInsert/adjustInsert pour la transformation)
		if (this._mem && invertOfOtId != null) {
			const deltaArray = this._mem[invertOfOtId];
			if (deltaArray != null) {
				delete this._mem[invertOfOtId];
				this._jumpInsertX(xa, len, deltaArray);
				return;
			}
		}
		this.adjustInsertX(xa, len);
	}

	rewindInsertX(xa: IXAddr, len: number) {
		this.mem = this._jumpDeleteX(xa, len, this.mem);
	}

	protected _jumpDeleteX(xa: IXAddr, len: number, memArray: Array<any>): Array<any> {
		//on mémorise les écarts pour le forward
		const oldXa = this.xa;
		const oldJml = this.jml;
		this.adjustDeleteX(xa, -len);
		if (this.xa.length < oldXa.length) {
			//on a été killed par le adjustDeleteX
			//On mémorise le sous-path qui permettra de réinsérer notre insertion au forward.
			return pushSubXa(memArray, this.xa, oldXa, oldJml);
		} else if (XA.isInSameSeq(xa, oldXa)) {
			//Same sequence
			const oldOffset = XA.last(oldXa) as number;
			const insOffs = XA.last(xa) as number;
			if (oldOffset > insOffs && oldOffset < insOffs + len) {
				//Notre insertion est dans le delete
				return pushPosInsInsX(memArray, 'i', oldOffset - insOffs);
			} else {
				//notre insertion est avant ou après le delete
				return pushPosInsInsX(memArray, oldOffset > insOffs ? 'a' : 'b');
			}
		} else {
			//Sequences différentes
			return pushPosInsInsX(memArray, 'o');
		}
	}

	forwardInsertX(xa: IXAddr, len: number) {
		this._jumpInsertX(xa, len, this.mem);
	}

	protected _jumpInsertX(xa: IXAddr, len: number, deltaArray: Array<any>) {
		const mem = popPosInsInsXOrSubXa(deltaArray);
		if (mem.subXa) {
			//L'insert enveloppait notre insertion
			if (len > 0) {
				//L'insert n'a pas été killed entre temps
				this.xa = applySubXa(xa, mem.subXa);
				this.jml = mem.content;
			} else {
				//L'insert qui nous enveloppe a été killed, on est donc nous même nécessairement déjà killed
				if (!this.killed) throw "XmlInsertMsg.forwardInsertX:: this not killed but wrapped insert killed!!!";
			}
			mem.content = null; //cleanup ram
		} else if (mem.pos === 'i') {
			//On réintègre un déplacement issu du rewind, si l'insert n'a pas été killed entre temps.
			const depth = xa.length - 1;
			if (len > 0) this.xa = XA.newBd(xa).setAtDepth(depth, (xa[depth] as number) + mem.startOffset).freeze();
		} else {
			if (len > 0) this.adjustInsertX(xa, len, mem.pos === 'b');
		}
	}

	invert() {
		return new XmlDeleteMsg().init(this.xa, this.len).copyStableMetasFrom(this);
	}

	inMigration(inMigr: boolean) {
		if (!inMigr) {
			delete this.mem;
			delete this._mem;
		}
	}

	assertMsgValidity(house: XmlHouse) {
		const doc = house.root;
		const ctn = XA.findDomContainer(this.xa, doc);
		if (!ctn) throw Error(`Container not found node for ${this} in :\n'${DOM.debug(doc)}'`);
		const idx = XA.last(this.xa);
		if (typeof idx === 'string') {
			throw Error(`Can not use XmlInsertMsg for setting attribute, use XmlStrMsg instead : ${this.xa}'`);
		} else if (ctn instanceof CharacterData) {
			if (idx < 0 || idx > ctn.data.length) throw Error(`Insert point in CharacterData node out of bound for ${this} in : '${DOM.debug(doc)}'`);
			if (typeof this.jml === 'object') throw Error(`Can not insert nodes in CharacterData node for ${this} in :\n'${DOM.debug(doc)}'`);
		} else if (ctn instanceof Attr) {
			if (idx < 0 || idx > ctn.value.length) throw Error(`Insert point in attribute out of bound for ${this} in : '${DOM.debug(doc)}'`);
			if (typeof this.jml === 'object') throw Error(`Can not insert nodes in an attribute for ${this} in :\n'${DOM.debug(doc)}'`);
		} else {
			if (idx < 0 || idx > ctn.childNodes.length) throw Error(`Insert point out of bound for ${this} in : '${DOM.debug(doc)}'`);
			if (typeof this.jml === 'string') throw Error(`Can not insert string in a non CharacterData node or attribute for ${this} in :\n'${DOM.debug(doc)}'`);
		}
	}

	toJson(json: any) {
		super.toJson(json);
		json.xa = this.xa;
		json.jml = this.jml;
		if (this.mem) json.mem = this.mem;
	}

	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromJson(json, factoryReg);
		this.xa = XA.freeze(json.xa);
		this.jml = json.jml;
		if (json.mem) this.mem = json.mem;
	}

	protected cloneFrom(o: this, purpose?: string) {
		super.cloneFrom(o, purpose);
		this.xa = o.xa;
		this.jml = o.jml;
		if (o.mem && purpose !== 'hist') this.mem = o.mem.concat();
	}

	toString() {
		//pour debug, on publie aussi les props non sérailisées (txt...)
		const json = this.asJson();
		if (this.otId) json.otId = this.otId;
		if (this._mem) json._mem = this._mem;
		return JSON.stringify(json);
	}
}

House.MSG_FACTORYREG.register(XmlInsertMsg);


/**
 * Suppression d'une séquence de texte (dans un attribut, un text, un comment, un PI)
 * ou d'une séquence de noeuds dans un élément ou le document.
 * {
 * xa: [1,3,0,"myAtt",5],
 * jml: "rem text"
 * }
 * {
 * xa: [1,5],
 * jml: [{'':"br"}]
 * }
 */
export class XmlDeleteMsg extends MsgOt implements IXmlPrgSensitiveMsg, IXmlMsg {
	static readonly type = "xmlDel";

	xa: IXAddr;
	len: number;

	jml: IJmlSet | string;
	protected mem: Array<any>;
	protected _mem: Dict<Array<any>>;

	init(xa: IXAddr, len: number): this {
		this.xa = XA.freeze(xa);
		if (XmlHouse.ASSERT) if (typeof XA.last(xa) === 'string') throw Error(`Can not use XmlDeleteMsg for attribute, use XmlStrMsg instead : ${this.xa}'`);
		this.len = len;
		return this;
	}

	/** mémoire du text supprimé pour construire un message inversé d'insertion. */
	setJml(jml: IJmlSet | string): this {
		this.jml = jml;
		return this;
	}

	get isUndoable() {return this.jml != null || this.killed}

	get isBodyMutator() {return true}

	get end() {return (XA.last(this.xa) as number) + this.len}

	get killed() {return this.len <= 0}

	kill() {this.xSetLen(0)}

	xSetLen(len: number) {
		this.len = len;
		this.jml = null; //le jml devient faux, il sera recalculé à l'update du body.
	}

	adjust(msg: IXmlPrgSensitiveMsg) {
		msg.adjustDeleteX(this.xa, -this.len);//TODO transformer api et passer this + hook en param pour msg de type List.
	}

	rewind(msg: IXmlPrgSensitiveMsg) {
		msg.rewindDeleteX(this.xa, -this.len);
	}

	forward(msg: IXmlPrgSensitiveMsg) {
		msg.forwardDeleteX(this.xa, -this.len);
	}

	cross(msg: IXmlPrgSensitiveMsg, invertOfOtId: OtId) {
		msg.crossDeleteX(this.xa, -this.len, this.otId);
	}

	get isMergeable(): boolean {return typeof this.jml === 'string'}

	mergeWith(nextMsg: MsgOt): MsgOt {
		if (this.jml == null) return null;
		if (nextMsg instanceof XmlDeleteMsg) {
			if (XA.isInSameSeq(this.xa, nextMsg.xa)) {
				if (typeof this.jml === 'string' && typeof nextMsg.jml === 'string') {
					const offset = XA.last(this.xa) as number;
					const offsetNext = XA.last(nextMsg.xa) as number;
					if (offset === offsetNext) {
						//ex : Suppr + Suppr
						this.jml += nextMsg.jml;
						this.len = this.jml.length;
						return this;
					}
					if (offsetNext + nextMsg.len === offset) {
						//ex : Backspace + Backspace
						this.xa = nextMsg.xa;
						this.jml = nextMsg.jml + this.jml;
						this.len = this.jml.length;
						return this;
					}
				}
			}
		}
		return null;
	}

	adjustDeleteX(xa: IXAddr, dLen: number) {
		const myDepth = this.xa.length - 1;
		const depthDel = xa.length - 1;
		for (let depth = 0, max = Math.min(depthDel, myDepth); depth <= max; depth++) {
			const offset = xa[depth];
			const myOffs = this.xa[depth];
			if (typeof offset === 'string' || typeof myOffs === 'string') {
				if (offset === myOffs) {
					// on entre dans le même attribut
					if (depth === depthDel) {
						//l'attr lui-même est supprimé
						this.xa = XA.freeze(xa);	// => On kill l'insert.
						this.kill();
						return;
					}
					continue;
				}
				return; //2 attributs != ou attr et child, pas d'interférence
			}
			if (depth === depthDel) {
				//On est à la profondeur du delete
				if (depth === myDepth) {
					//Le delete et notre suppr sont dans la même séquence
					const otherEnd = offset - dLen;
					const myEnd = this.end;
					if (otherEnd > myOffs && offset < myEnd) {
						this.xSetLen(Math.max(0, offset - myOffs) + Math.max(0, myEnd - otherEnd));
					}
					if (offset < myOffs) this.xa = XA.newBd(this.xa).setAtDepth(depth, Math.max(myOffs + dLen, offset)).freeze();
				} else {
					//Notre suppr est dans un sous-noeud par rapport au delete.
					if (offset > myOffs) return; 		//Le delete est après notre suppr, pas d'interférence.
					if (offset - dLen <= myOffs) {	//Le delete est intégralement avant notre suppr, on décale
						this.xa = XA.newBd(this.xa).setAtDepth(depth, myOffs + dLen).freeze();
					} else {
						//Notre suppr est dans le delete
						this.kill();				// => On kill le delete.
						this.xa = XA.freeze(xa);
					}
				}
			} else {
				//On est encore dans un container du delete
				if (offset != myOffs) return;	//Le delete concerne une autre séquence que notre suppr, pas d'interférence.
			}
		}
	}

	crossDeleteX(xa: IXAddr, dLen: number, fromOtId: OtId) {
		if (!this._mem) this._mem = Object.create(null);
		this._mem[fromOtId] = this._jumpDeleteX(xa, -dLen, this._mem[fromOtId]);
	}

	rewindDeleteX(xa: IXAddr, dLen: number) {
		this.adjustInsertX(xa, -dLen);
	}

	forwardDeleteX(xa: IXAddr, dLen: number) {
		this.adjustDeleteX(xa, dLen)
	}

	adjustInsertX(xa: IXAddr, len: number) {
		const myDepth = this.xa.length - 1;
		const depthDel = xa.length - 1;
		for (let depth = 0, max = Math.min(depthDel, myDepth); depth <= max; depth++) {
			const offset = xa[depth];
			const myOffs = this.xa[depth];
			if (typeof offset === 'string' || typeof myOffs === 'string') {
				if (offset === myOffs) continue; // on entre dans le même attribut
				return; //2 attributs != ou attr et child, pas d'interférence
			}
			if (depth === depthDel) {
				//On est à la profondeur de l'insert
				if (depth === myDepth) {
					//L'insert et notre suppr sont dans la même séquence
					if (offset <= myOffs) {			//L'insert est avant notre suppr, on décale
						this.xa = XA.newBd(this.xa).setAtDepth(depth, myOffs + len).freeze();
					} else {
						if (offset < this.end) {	//L'insert est dans notre suppr, on absorbe cet insert.
							this.xSetLen(this.len + len);
						}
					}
				} else {
					//Notre suppr est dans un sous-noeud de la séquence de l'insert
					if (offset <= myOffs) {			//L'insert est avant notre suppr, on décale
						this.xa = XA.newBd(this.xa).setAtDepth(depth, myOffs + len).freeze();
					}
				}
			} else {
				//On est encore dans un container de l'insert
				if (offset !== myOffs) return;	//L'insert concerne une autre séquence que notre suppr, pas d'interférence.
			}
		}
	}

	crossInsertX(xa: IXAddr, len: number, invertOfOtId: OtId) {
		//(~= rewindDelete pour la mémoire + forwardInsert/adjustInsert pour la transformation)
		if (this._mem && invertOfOtId != null) {
			const posArray = this._mem[invertOfOtId];
			if (posArray) {
				delete this._mem[invertOfOtId];
				this._jumpInsertX(xa, len, posArray);
				return;
			}
		}
		this.adjustInsertX(xa, len);
	}

	rewindInsertX(xa: IXAddr, len: number) {
		this.mem = this._jumpDeleteX(xa, len, this.mem);
	}

	protected _jumpDeleteX(xa: IXAddr, len: number, memArray: Array<any>) {
		//on mémorise les écarts pour le forward
		const oldXa = this.xa;
		const oldLen = this.len;
		this.adjustDeleteX(xa, -len);
		if (this.xa.length < oldXa.length) {
			//Notre suppr a été killed par adjustDeleteX
			//On mémorise le sous-path qui permettra de réexecuter notre suppr dans l'insert au forward.
			return pushSubXa(memArray, this.xa, oldXa, oldLen);
		} else if (XA.isInSameSeq(this.xa, xa)) {
			//Notre suppr se trouve dans la même séquence que l'insert et se chevauche
			//On mémorise la situation complète (cf slide d'architecure).
			const myOffs = XA.last(oldXa) as number;
			const myEnd = myOffs + oldLen;
			const offset = XA.last(xa) as number;
			const otherEnd = offset + len;
			return pushPosInsDelX(memArray,
				myOffs < offset, myOffs >= otherEnd, myOffs - offset,
				myEnd <= offset, myEnd > otherEnd, otherEnd - myEnd);
		} else {
			//Notre suppr a juste été éventuellement décalée par cet insert
			//On mémorise le delta pour le réappliquer au forward
			return pushDeltaX(memArray, oldXa, this.xa);
		}
	}

	forwardInsertX(xa: IXAddr, len: number) {
		this._jumpInsertX(xa, len, this.mem);
	}

	protected _jumpInsertX(xa: IXAddr, len: number, memArray: Array<any>) {
		const mem = popPosInsDelXOrSubXa(memArray);
		if (mem.subXa != null) {
			//L'insert enveloppait notre suppr
			if (len > 0) {
				//L'insert n'a pas été killed entre temps
				this.xa = applySubXa(xa, mem.subXa);
				this.xSetLen(mem.content);
			} else {
				//L'insert qui nous enveloppe a été killed, on est donc nous même nécessairement déjà killed
				if (!this.killed) throw "XmlDeleteMsg._jumpInsertX:: this not killed but wrapped insert killed!!!";
			}
		} else if (mem.delta != null) {
			//On réintègre un déplacement issu du rewind, si l'insert n'a pas été killed entre temps.
			if (len !== 0 && mem.delta !== 0) this.xa = XA.newBd(this.xa).setAtDepth(mem.depth, (this.xa[mem.depth] as number) + mem.delta).freeze();
		} else if (len > 0) {
			//on était dans la même séquence, gestion d'un chevauchement.
			const depth = xa.length - 1;
			const offset = xa[depth] as number;
			if (mem.startAfter) { //cas 1 (cf slide dans archi.odp)
				//insertion avant notre suppr : c'est le start qu'on décale
				this.xa = XA.newBd(this.xa).setAtDepth(depth, (this.xa[depth] as number) + len).freeze();
			} else if (mem.endBefore) { //cas 2
				//insertion après notre suppr, rien à faire
			} else if (mem.startBefore) { // cas 3
				if (mem.endAfter) { // cas 3.1
					//notre suppr enveloppe totalement l'insertion
					this.xSetLen(this.len + len);
				} else { //cas 3.2
					//notre suppr commence avant mais termine dans l'insertion
					this.xSetLen(len - mem.endOffset + offset - (this.xa[depth] as number));
				}
			} else { // cas 4
				//notre suppr commence dans l'insertion...
				this.xa = XA.newBd(this.xa).setAtDepth(depth, offset + mem.startOffset).freeze();
				if (mem.endAfter) { //cas 4.1
					///... et termine après l'insertion
					this.xSetLen(this.len + len - mem.startOffset);
				} else { //cas 4.2
					///... et termine dans l'insertion
					this.xSetLen(this.len + len - mem.startOffset - mem.endOffset);
				}
			}
		} else {
			//console.log("jump insert killed::"+this+"\nmem:::"+JSON.stringify(mem));
		}
	}

	invert() {
		if (XmlHouse.ASSERT) if (this.jml == null && !this.killed) throw Error("jml not available for undo/redo");
		return new XmlInsertMsg().init(this.xa, this.killed ? null : this.jml).copyStableMetasFrom(this);
	}

	inMigration(inMigr: boolean) {
		if (!inMigr) {
			delete this.mem;
			delete this._mem;
		}
	}

	assertMsgValidity(house: XmlHouse) {
		const doc = house.root;
		const ctn = XA.findDomContainer(this.xa, doc);
		if (!ctn) throw Error(`Container not found node for ${this} in :\n${DOM.debug(doc)}`);
		const idx = XA.last(this.xa);
		const len = this.len;
		if (typeof idx === 'string') {
			throw Error(`Can not use XmlDeleteMsg for attribute, use XmlStrMsg instead : ${this}`);
		} else if (ctn instanceof CharacterData) {
			if (idx < 0 || idx > ctn.data.length) throw Error(`Delete point in CharacterData node out of bound for ${this} in : '${DOM.debug(doc)}`);
			if (len < 0 || idx + len > ctn.data.length) throw Error(`Delete length in CharacterData node out of bound for  ${this} in :\n'${DOM.debug(doc)}`);
		} else if (ctn instanceof Attr) {
			if (idx < 0 || idx > ctn.value.length) throw Error(`Delete point in attribute out of bound for ${this} in : '${DOM.debug(doc)}'`);
			if (len < 0 || idx + len > ctn.value.length) throw Error(`Delete length in Attribute node out of bound for  ${this} in :\n${DOM.debug(doc)}`);
		} else {
			if (idx < 0 || idx > ctn.childNodes.length) throw Error(`Delete point out of bound for ${this} in :\n${DOM.debug(doc)}`);
			if (len < 0 || idx + len > ctn.childNodes.length) throw Error(`Delete length out of bound for  ${this} in :\n${DOM.debug(doc)}`);
		}
		if (this.jml != null && JML.lengthJmlOrText(this.jml) !== this.len) throw Error(`msg.len and msg.jml prorpeties are out of sync in ${this}`);
	}

	toJson(json: any) {
		super.toJson(json);
		json.xa = this.xa;
		json.len = this.len;
		if (this.mem) json.mem = this.mem;
	}

	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromJson(json, factoryReg);
		this.xa = XA.freeze(json.xa);
		this.len = json.len;
		if (json.mem) this.mem = json.mem;
	}

	protected cloneFrom(o: this, purpose?: string) {
		super.cloneFrom(o, purpose);
		this.xa = o.xa;
		this.len = o.len;
		if (purpose === 'hist') {
			this.jml = o.jml;
		} else {
			if (o.mem) this.mem = o.mem.concat();
		}
	}

	toString() {
		//pour debug, on publie aussi les props non sérailisées (jml...)
		const json = this.asJson();
		if (this.jml) json.jml = this.jml;
		if (this.otId) json.otId = this.otId;
		if (this._mem) json._mem = this._mem;
		return JSON.stringify(json);
	}
}

House.MSG_FACTORYREG.register(XmlDeleteMsg);


/**
 * Liste de Msg implémentant l'api XmlMsg
 */
export class XmlListMsgOt extends ListMsgOt implements IXmlPrgSensitiveMsg {
	static readonly type: string = "xmlList";

	msgs: Array<IXmlPrgSensitiveMsg>;

	adjustInsertX(xa: IXAddr, len: number) {
		this.msgs.forEach(m => {
			m.adjustInsertX(xa, len)
		});
	}

	crossInsertX(xa: IXAddr, len: number, invertOfOtId: OtId) {
		this.msgs.forEach(m => {
			m.crossInsertX(xa, len, invertOfOtId)
		});
	}

	rewindInsertX(xa: IXAddr, len: number) {
		this.msgs.forEach(m => {
			m.rewindInsertX(xa, len)
		});
	}

	forwardInsertX(xa: IXAddr, len: number) {
		this.msgs.forEach(m => {
			m.forwardInsertX(xa, len)
		});
	}

	adjustDeleteX(xa: IXAddr, dLen: number) {
		this.msgs.forEach(m => {
			m.adjustDeleteX(xa, dLen)
		});
	}

	crossDeleteX(xa: IXAddr, dLen: number, fromOtId: OtId) {
		this.msgs.forEach(m => {
			m.crossDeleteX(xa, dLen, fromOtId)
		});
	}

	rewindDeleteX(xa: IXAddr, dLen: number) {
		this.msgs.forEach(m => {
			m.rewindDeleteX(xa, dLen)
		});
	}

	forwardDeleteX(xa: IXAddr, dLen: number) {
		this.msgs.forEach(m => {
			m.forwardDeleteX(xa, dLen)
		});
	}

}

House.MSG_FACTORYREG.register(XmlListMsgOt);


export class XmlBatch extends XmlListMsgOt {
	static readonly type: string = "xmlBatch";

	selBefore: XmlRangeMsg;
	selAfter: XmlRangeMsg;

	protected _done: boolean;

	protected _adjustUntil = 0;

	constructor() {
		super();
		this.msgs = [];
	}

	getState() {
		if (this._done) return 'executed';
		if (this.msgs.length === 0) return 'empty';
		return 'building';
	}

	setSelBefore(start: IXAddr | null, end?: IXAddr): this {
		if (this._done) throw Error("Batch already executed.");
		this.selBefore = start ? new XmlRangeMsg().init(start, end) : null;
		return this;
	}

	setSelBeforeSeq(start: IXAddr, len: number): this {
		this.setSelBefore(start);
		this.selBefore.end = XA.freeze(XA.incrAtDepth(this.selBefore.addr, -1, len));
		return this;
	}

	setSelAfterRange(val: IXAddrRange): this {
		if (val) this.setSelAfter(val.start, val.end);
		else this.setSelAfter(null);
		return this;
	}

	setSelAfterSeq(start: IXAddr, len: number): this {
		this.setSelAfter(start);
		//On reaffecte la len après les ajustements du point de départ.
		//évite une modif de la len liés aux ajustements.
		this.selAfter.end = XA.freeze(XA.incrAtDepth(this.selAfter.addr, -1, len));
		return this;
	}

	setSelAfter(start: IXAddr, end?: IXAddr): this {
		if (this._done) throw Error("Batch already executed.");
		if (start) {
			this.selAfter = new XmlRangeMsg().init(start, end);
			for (let i = 0; i < this._adjustUntil; i++) this.msgs[i].adjust(this.selAfter);
		} else {
			this.selAfter = null;
		}
		return this;
	}

	setSelAfterSkipAdjust(start: IXAddr, end?: IXAddr): this {
		if (this._done) throw Error("Batch already executed.");
		if (start) {
			this.selAfter = new XmlRangeMsg().init(start, end);
		} else {
			this.selAfter = null;
		}
		return this;
	}

	extendSelAfterRange(val: IXAddrRange): this {
		return this.extendSelAfter(val.start, val.end);
	}

	extendSelAfterSeq(start: IXAddr, len: number): this {
		return this.extendSelAfter(start, XA.newBd(start).incrAtDepth(-1, len).xa);
	}

	extendSelAfter(start: IXAddr, end?: IXAddr): this {
		if (this._done) throw Error("Batch already executed.");
		if (this.selAfter == null) {
			this.setSelAfter(start, end);
			return this;
		}
		if (this._adjustUntil > 0) {
			const r = new XmlRangeMsg().init(start, end);
			for (let i = 0; i < this._adjustUntil; i++) this.msgs[i].adjust(r);
			start = r.addr;
			end = r.end;
		}
		if (XA.isBefore(start, this.selAfter.addr)) {
			this.selAfter.addr = XA.freeze(start);
		}
		if (end && XA.isBefore(this.selAfter.end, end)) {
			this.selAfter.end = XA.freeze(end);
		}
		return this;
	}

	needAdjustForNextAdds(): this {
		this._adjustUntil = this.msgs.length;
		return this;
	}

	add(msg: IXmlPrgSensitiveMsg): this {
		for (let i = 0; i < this._adjustUntil; i++) this.msgs[i].adjust(msg);
		this.msgs.push(msg);
		if (this.selAfter) msg.adjust(this.selAfter);
		return this;
	}

	addAll(msgs: IXmlPrgSensitiveMsg[]): this {
		msgs.forEach((m) => this.add(m));
		return this;
	}

	setDone() {
		this._done = true;
	}

	adjustDeleteX(xa: IXAddr, dLen: number) {
		if (this.selBefore) this.selBefore.adjustDeleteX(xa, dLen);
		super.adjustDeleteX(xa, dLen);
		if (this.selAfter) this.selAfter.adjustDeleteX(xa, dLen);
	}

	crossDeleteX(xa: IXAddr, dLen: number, fromOtId: OtId) {
		if (this.selBefore) this.selBefore.crossDeleteX(xa, dLen, fromOtId);
		super.crossDeleteX(xa, dLen, fromOtId);
		if (this.selAfter) this.selAfter.crossDeleteX(xa, dLen, fromOtId);
	}

	rewindDeleteX(xa: IXAddr, dLen: number) {
		if (this.selBefore) this.selBefore.rewindDeleteX(xa, dLen);
		super.rewindDeleteX(xa, dLen);
		if (this.selAfter) this.selAfter.rewindDeleteX(xa, dLen);
	}

	forwardDeleteX(xa: IXAddr, dLen: number) {
		if (this.selBefore) this.selBefore.forwardDeleteX(xa, dLen);
		super.forwardDeleteX(xa, dLen);
		if (this.selAfter) this.selAfter.forwardDeleteX(xa, dLen);
	}

	adjustInsertX(xa: IXAddr, len: number) {
		if (this.selBefore) this.selBefore.adjustInsertX(xa, len);
		super.adjustInsertX(xa, len);
		if (this.selAfter) this.selAfter.adjustInsertX(xa, len);
	}

	crossInsertX(xa: IXAddr, len: number, invertOfOtId: OtId) {
		if (this.selBefore) this.selBefore.crossInsertX(xa, len, invertOfOtId);
		super.crossInsertX(xa, len, invertOfOtId);
		if (this.selAfter) this.selAfter.crossInsertX(xa, len, invertOfOtId);
	}

	rewindInsertX(xa: IXAddr, len: number) {
		if (this.selBefore) this.selBefore.rewindInsertX(xa, len);
		super.rewindInsertX(xa, len);
		if (this.selAfter) this.selAfter.rewindInsertX(xa, len);
	}

	forwardInsertX(xa: IXAddr, len: number) {
		if (this.selBefore) this.selBefore.forwardInsertX(xa, len);
		super.forwardInsertX(xa, len);
		if (this.selAfter) this.selAfter.forwardInsertX(xa, len);
	}

	invert(): XmlBatch {
		const msg = super.invert() as XmlBatch;
		msg.selBefore = this.selAfter;
		msg.selAfter = this.selBefore;
		return msg.copyStableMetasFrom(this);
	}

	inMigration(inMigr: boolean) {
		if (inMigr) {
			if (this.inMigr) return; //déjà en migration
			if (this.selAfter) {
				this.selAfter.inMigration(true);
				for (let i = this.msgs.length - 1; i >= 0; i--) {
					this.msgs[i].rewind(this.selAfter);
				}
			}
			if (this.selBefore) this.selBefore.inMigration(true);
		} else {
			if (!this.inMigr) return; //déjà sorti de la migration
			if (this.selBefore) this.selBefore.inMigration(false);
		}
		super.inMigration(inMigr);
		if (!inMigr) {
			if (this.selAfter) {
				for (let i = 0, s = this.msgs.length; i < s; i++) {
					this.msgs[i].forward(this.selAfter);
				}
				this.selAfter.inMigration(false);
			}
		}
	}

	toJson(json: any) {
		super.toJson(json);
		if (this.selBefore) json.selBefore = this.selBefore.asJson();
		if (this.selAfter) json.selAfter = this.selAfter.asJson();
	}

	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromJson(json, factoryReg);
		if (json.selBefore) this.selBefore = factoryReg.newFromJson(json.selBefore) as XmlRangeMsg;
		if (json.selAfter) this.selAfter = factoryReg.newFromJson(json.selAfter) as XmlRangeMsg;
	}

	protected cloneFrom(o: this, purpose?: string) {
		super.cloneFrom(o, purpose);
		this.selBefore = o.selBefore ? o.selBefore.clone(purpose) : null;
		this.selAfter = o.selAfter ? o.selAfter.clone(purpose) : null;
	}
}

House.MSG_FACTORYREG.register(XmlBatch);

/**
 * Implémentation d'un move d'une séquence par un simple enchainement d'un delete et d'un insert.
 *
 * ATTENTION pas satisfaisant en asynch dans le cas de suppr ou autre move concurrent :
 * les adjust intermédaires peuvent faire évoluer la zone supprimée et le report de ces changements n'est pas réalisée
 * dans la zone insérée.
 * Cette prise en compte de la variation de la supp sur l'ins est complexe à cause du mécanisme de rewind/forward
 * qui ne peut admettre de variation de son contenu inséré (les offsets dans la mem du rewind ne suivent pas ces ajustements).
 */
export class XmlMoveMsg extends XmlListMsgOt {
	static readonly type = "xmlMove";

	/**
	 *
	 * @param xa Adresse du début de la séquence à déplacer
	 * @param jmlOrText Contenu de la séquence à déplacer (devrait être une simple length pour une impl parfaite...)
	 * @param xaTarget Cible / point d'insertion du move.
	 */
	init(xa: IXAddr, jmlOrText: IJmlSet | string, xaTarget: IXAddr): this {
		const len = JML.lengthJmlOrText(jmlOrText);
		const target = XA.translateDelSeq(xaTarget, xa, len);
		return super.initList([
			XA.isAttribute(xa) ? new XmlStrMsg().init(xa, null) : new XmlDeleteMsg().init(xa, len),
			XA.isAttribute(target) ? new XmlStrMsg().init(target, jmlOrText as string) : new XmlInsertMsg().init(target, jmlOrText)
		]);
	}
}

House.MSG_FACTORYREG.register(XmlMoveMsg);

/**
 * Split simple (un seul niveau) d'un Element ou CharacterData.
 *
 * ATTENTION pas satisfaisant en asynch dans le cas de modification concurrente.
 */
export class XmlSplitMsg extends XmlListMsgOt {
	static readonly type = "xmlSplit";


	/**
	 * @param xa Addresse du point de départ du split
	 * @param lenToRemove longueur qui sera supprimée entre xa et jmlToMove.
	 *              Usage : généralement ce contenu correspondant à la sélection est réintégrée dans jmlToInject,
	 * @param jmlToMove Contenu de la fin de séquence à déplacer (devrait être une simple length pour une impl parfaite...)
	 * @param newCtn Nouveau container à injecter en frère suivant du container de xa : [""] ou [{'':"tag", 'class':"..."}] ou [{'':"!"}]...
	 *              Usage : permet de créer le nouveau container incluant des metas clonés du 1er.
	 * @param relAddr Addresse relative d'insertion de jmlOrText dans newCtn, par défaut : [0]
	 *              Usage : permet de bien placer le contenu à déplacer dans newCtn.
	 * @param jmlToInject Contenu à injecter dans l'interstice créé (après xa, avant newCtn), peut-être null.
	 */
	init(xa: IXAddr, lenToRemove: number, jmlToMove: IJmlSet | string, newCtn: IJmlSet, relAddr: IXAddr, jmlToInject: IJmlSet): this {
		const len = JML.lengthJmlOrText(jmlToMove);
		const nextSibling = XA.newBd(xa).up().incrAtDepth(-1, 1).freeze();
		if (!relAddr) relAddr = XmlSplitMsg._XADDR0;
		const msgs = [
			new XmlInsertMsg().init(nextSibling, newCtn),
			//ou new XmlMoveMsg().init(xa, jmlOrText, XA.append(nextSibling, ...relAddr)) si impl primaire valide
			new XmlDeleteMsg().init(xa, lenToRemove + len),
			new XmlInsertMsg().init(XA.append(nextSibling, ...relAddr), jmlToMove)
		];
		if (jmlToInject) msgs.push(new XmlInsertMsg().init(nextSibling, jmlToInject));
		return super.initList(msgs);
	}

	protected static _XADDR0 = Object.freeze([0]) as IXAddr;
}

House.MSG_FACTORYREG.register(XmlSplitMsg);

/**
 * Contrairement à XmlInsertMsg et XmlDeleteMsg, XmlStrMsg <b>écrase</b> la valeur entière de l'attribut
 * ou d'un noeud character-data (text, comment, pi).
 *
 * Si la cible est un attribut, ce message doit être utilisé pour créer et supprimer un attribut et,
 * lorsque l'attribut existe, pour modifier une valeur atomique (énum, date, nombre...).
 *
 * {
 * xa: [1,3, "myAttr"],
 * val : "newVal",
 * old : ""
 * }
 * Pour une suppr d'un attr :
 * {
 * xa: [1,3, "myAttr"],
 * val : null,
 * old : ""
 * }
 *
 * Si la cible est un noeud (text, comment, pi) ce message doit être utilisé lorsqu'il existe pour modifier
 * une valeur atomique (énum, date, nombre...). Ce message NE PEUT PAS être utilisé pour créer ou supprimer
 * le noeud.
 *
 */
export class XmlStrMsg extends MsgOt implements IXmlPrgSensitiveMsg, IXmlMsg {
	static readonly type = "xmlStr";

	xa: IXAddr;
	protected _val: string | null | false /*msg killed*/;
	protected old: string;

	protected mem: Array<any>;
	protected _mem: Dict<any>;

	init(xa: IXAddr, val: string | null | false /* false: usage interne*/): this {
		this.xa = XA.freeze(xa);
		this._val = val;
		if (XmlHouse.ASSERT) if (val == null && typeof XA.last(xa) !== 'string') throw Error("XmlStrMsg can not be used for deleting a node, only attribute");
		return this;
	}

	get val() {
		return this._val === false ? null : this._val;
	}

	setOld(val: string): this {
		this.old = val;
		return this;
	}

	get isUndoable() {return "old" in this}

	get isBodyMutator() {return true}

	get killed() {return this._val === false}

	kill() {
		this._val = false;
		this.old = null;
	}

	adjust(msg: IXmlPrgSensitiveMsg) {
		if (this.killed) return;
		switch (msg.type) {
		case XmlInsertMsg.type :
		case XmlDeleteMsg.type :
			if (XA.isAnc(this.xa, (msg as XmlInsertMsg | XmlDeleteMsg).xa)) msg.kill();
			break;
		}
	}

	rewind(msg: IXmlPrgSensitiveMsg) {
		msg.rewindInsertX(this.xa, this.killed ? 0 : 1);
		//msg.rewindDeleteX(this.xa, -1);
	}

	forward(msg: IXmlPrgSensitiveMsg) {
		//msg.forwardDeleteX(this.xa, -1);
		msg.forwardInsertX(this.xa, this.killed ? 0 : 1);
	}

	cross(msg: IXmlPrgSensitiveMsg, invertOfOtId: OtId) {
		const len = this.killed ? 0 : 1;
		msg.crossDeleteX(this.xa, -len, this.otId);
		msg.crossInsertX(this.xa, len, invertOfOtId);
	}

	get isMergeable(): boolean {return true}

	mergeWith(nextMsg: MsgOt): MsgOt {
		if (nextMsg instanceof XmlStrMsg) return XA.isEquals(this.xa, nextMsg.xa) ? nextMsg : null;
		return null;
	}

	adjustDeleteX(xa: IXAddr, dLen: number) {
		const oldXa = this.xa;
		this.xa = XA.freeze(XA.translateDelSeq(this.xa, xa, -dLen, false));
		if (this.xa.length < oldXa.length) {
			//on était un descendant du fragment supprimé, donc on est killed.
			this.kill();
		}
	}

	crossDeleteX(xa: IXAddr, dLen: number, fromOtId: OtId) {
		if (!this._mem) this._mem = Object.create(null);
		this._mem[fromOtId] = this._jumpDeleteX(xa, -dLen, this._mem[fromOtId]);
	}

	rewindDeleteX(xa: IXAddr, dLen: number) {
		this.adjustInsertX(xa, -dLen);
	}

	forwardDeleteX(xa: IXAddr, dLen: number) {
		this.adjustDeleteX(xa, dLen);
	}

	adjustInsertX(xa: IXAddr, len: number) {
		this.xa = XA.freeze(XA.translateInsSeq(this.xa, xa, len));
	}

	crossInsertX(xa: IXAddr, len: number, invertOfOtId: OtId) {
		if (this._mem && invertOfOtId != null) {
			const memArray = this._mem[invertOfOtId];
			if (memArray != null) {
				delete this._mem[invertOfOtId];
				this._jumpInsertX(xa, len, memArray);
				return;
			}
		}
		this.adjustInsertX(xa, len);
	}

	rewindInsertX(xa: IXAddr, len: number) {
		this.mem = this._jumpDeleteX(xa, len, this.mem);
	}

	protected _jumpDeleteX(xa: IXAddr, len: number, memArray: Array<any>) {
		//on mémorise les écarts pour le forward
		const oldXa = this.xa;
		const oldVal = this.val;
		//this.adjustDeleteX(xa, -len);
		this.xa = XA.freeze(XA.translateDelSeq(this.xa, xa, len, false));
		if (this.xa.length < oldXa.length) {
			//on était un descendant du fragment supprimé (rewind de l'insert)
			//On mémorise le sous-path qui permettra de réinsérer notre attr au forward.
			return pushSubXa(memArray, this.xa, oldXa, oldVal);
		} else {
			//On mémorise un éventuel décalage à réappliquer au forward.
			return pushDeltaX(memArray, oldXa, this.xa);
		}
	}

	forwardInsertX(xa: IXAddr, len: number) {
		this._jumpInsertX(xa, len, this.mem);
	}

	protected _jumpInsertX(xa: IXAddr, len: number, memArray: Array<any>) {
		const mem = popDeltaXOrSubXa(memArray);
		if (mem.subXa) {
			//L'insert enveloppait notre setAttr
			if (len > 0) {
				//L'insert n'a pas été killed entre temps
				this.xa = applySubXa(xa, mem.subXa);
				this._val = mem.content;
			} else {
				//L'insert qui nous enveloppe a été killed, on est donc nous même nécessairement déjà killed
				if (!this.killed) throw "XmlStrMsg.forwardInsertX:: this not killed but wrapped insert killed!!!";
			}
			mem.content = null; //cleanup ram
		} else if (mem.delta != 0) {
			//On réintègre un déplacement issu du rewind, si l'insert n'a pas été killed entre temps.
			if (len > 0) this.xa = XA.newBd(this.xa).setAtDepth(mem.depth, (this.xa[mem.depth] as number) + mem.delta).freeze();
		}
	}

	invert() {
		if (XmlHouse.ASSERT) if (this.old === undefined && !this.killed) throw Error("old str value not available for invert");
		return new XmlStrMsg().init(this.xa, this.killed ? false : this.old).copyStableMetasFrom(this);
	}

	inMigration(inMigr: boolean) {
		if (!inMigr) {
			delete this.mem;
			delete this._mem;
		}
	}

	toJson(json: any) {
		super.toJson(json);
		json.xa = this.xa;
		json.val = this._val;
		if (this.mem) json.mem = this.mem;
	}

	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromJson(json, factoryReg);
		this.xa = XA.freeze(json.xa);
		this._val = json.val;
		if (json.mem) this.mem = json.mem;
	}

	protected cloneFrom(o: this, purpose?: string) {
		super.cloneFrom(o, purpose);
		this.xa = o.xa;
		this._val = o._val;
		if (purpose === 'hist') {
			this.old = o.old;
		} else {
			if (o.mem) this.mem = o.mem.concat();
		}
	}

	assertMsgValidity(house: XmlHouse) {
		const doc = house.root;
		if (!XA.isAttribute(this.xa)) {
			const target = XA.findDomLast(this.xa, doc);
			if (!target) throw Error(`Last node not found node for '${this}' in :\n${DOM.debug(doc)}`);
			if (!(target instanceof CharacterData)) throw Error(`Can not use XmlStrMsg '${this}' on this node: ${DOM.debug(target)}`);
		} else {
			const ctn = XA.findDomContainer(this.xa, doc);
			if (!ctn) throw Error(`Container not found node for '${this}' in :\n${DOM.debug(doc)}`);
		}
	}

	toString() {
		//pour debug, on publie aussi les props non sérailisées
		const json = this.asJson();
		json.old = this.old;
		json.otId = this.otId;
		if (this._mem) json._mem = this._mem;
		return JSON.stringify(json);
	}
}

House.MSG_FACTORYREG.register(XmlStrMsg);

/** Message de modification d'une simple addresse, XmlAddrState. */
export class XmlAddrMsg extends MsgOt implements IXmlPrgSensitiveMsg {
	static readonly type: string = "xmlAddr";

	addr: IXAddr;
	killed: boolean = false;

	protected mem: Array<any>;
	protected _mem: Dict<any>;

	init(addr: IXAddr): this {
		this.addr = XA.freeze(addr);
		return this;
	}

	kill() {
		this.killed = true;
	}

	get isMergeable(): boolean {return false}//TODO fusion de XmlRangeMsg le jour où XmlRangeState sera exploité pour synchro des selections

	adjustDeleteX(xa: IXAddr, dLen: number) {
		this.addr = XA.freeze(XA.translateDelSeq(this.addr, xa, -dLen));
	}

	crossDeleteX(xa: IXAddr, dLen: number, fromOtId: OtId) {
		if (!this._mem) this._mem = Object.create(null);
		this._mem[fromOtId] = this._jumpDeleteX(xa, -dLen, this._mem[fromOtId]);
	}

	rewindDeleteX(xa: IXAddr, dLen: number) {
		this.adjustInsertX(xa, -dLen);
	}

	forwardDeleteX(xa: IXAddr, dLen: number) {
		this.adjustDeleteX(xa, dLen);
	}

	adjustInsertX(xa: IXAddr, len: number, biasBefore?: boolean) {
		this.addr = XA.freeze(XA.translateInsSeq(this.addr, xa, len, true));
	}

	crossInsertX(xa: IXAddr, len: number, invertOfOtId: OtId) {
		//(~= rewindDelete pour la mémoire + forwardInsert/adjustInsert pour la transformation)
		if (this._mem && invertOfOtId != null) {
			const deltaArray = this._mem[invertOfOtId];
			if (deltaArray != null) {
				delete this._mem[invertOfOtId];
				this._jumpInsertX(xa, len, deltaArray);
				return;
			}
		}
		this.adjustInsertX(xa, len);
	}

	rewindInsertX(xa: IXAddr, len: number) {
		this.mem = this._jumpDeleteX(xa, len, this.mem);
	}

	protected _jumpDeleteX(xa: IXAddr, len: number, memArray: Array<any>): Array<any> {
		//on mémorise les écarts
		const oldStart = this.addr;
		this.adjustDeleteX(xa, -len);
		return this._pushXaDiff(oldStart, xa, len, this.addr, memArray);
	}

	protected _pushXaDiff(oldXa: IXAddr, xaDel: IXAddr, lenDel: number, newXa: IXAddr, memArray: Array<any>): Array<any> {
		if (newXa.length < oldXa.length) {
			//on a été killed par le adjustDeleteX
			//On mémorise le sous-path qui permettra de repositionner la sel au forward.
			return pushSubXa(memArray, newXa, oldXa, null);
		} else if (XA.isInSameSeq(xaDel, oldXa)) {
			//Same sequence
			const oldOffset = XA.last(oldXa) as number;
			const insOffs = XA.last(xaDel) as number;
			if (oldOffset > insOffs && oldOffset < insOffs + lenDel) {
				//Notre insertion est dans le delete
				return pushPosInsInsX(memArray, 'i', oldOffset - insOffs);
			} else {
				//notre insertion est avant ou après le delete
				return pushPosInsInsX(memArray, oldOffset > insOffs ? 'a' : 'b');
			}
		} else {
			//Sequences différentes
			return pushPosInsInsX(memArray, 'o');
		}
	}

	forwardInsertX(xa: IXAddr, len: number) {
		this._jumpInsertX(xa, len, this.mem);
	}

	protected _jumpInsertX(xa: IXAddr, len: number, deltaArray: Array<any>) {
		this.addr = this._popXaDiff(this.addr, xa, len, deltaArray);
	}

	protected _popXaDiff(xa: IXAddr, xaIns: IXAddr, len: number, deltaArray: Array<any>): IXAddr {
		const mem = popPosInsInsXOrSubXa(deltaArray);
		if (mem.subXa) {
			//L'insert enveloppait notre insertion
			if (len > 0) {
				//L'insert n'a pas été killed entre temps
				return applySubXa(xaIns, mem.subXa);
			}
		} else if (mem.pos === 'i') {
			//On réintègre un déplacement issu du rewind, si l'insert n'a pas été killed entre temps.
			const depth = xaIns.length - 1;
			if (len > 0) return XA.newBd(xaIns).setAtDepth(depth, (xaIns[depth] as number) + mem.startOffset).freeze();
		} else {
			if (len > 0) return XA.freeze(XA.translateInsSeq(xa, xaIns, len, mem.pos === 'b'));
		}
		return xa;
	}

	invert(): XmlAddrMsg {
		//simple clone.
		return new XmlAddrMsg().init(this.addr).copyStableMetasFrom(this);
	}

	inMigration(inMigr: boolean) {
		if (!inMigr) {
			delete this.mem;
			delete this._mem;
		}
	}

	toJson(json: any) {
		super.toJson(json);
		json.start = this.addr;
		if (this.mem) json.mem = this.mem;
	}

	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromJson(json, factoryReg);
		this.addr = XA.freeze(json.start);
		if (json.mem) this.mem = json.mem;
	}

	protected cloneFrom(o: this, purpose?: string) {
		super.cloneFrom(o, purpose);
		this.addr = o.addr;
		if (o.mem && purpose !== 'hist') this.mem = o.mem.concat();
	}

	toString() {
		//pour debug, on publie aussi les props non sérailisées (txt...)
		const json = this.asJson();
		if (this.otId) json.otId = this.otId;
		if (this._mem) json._mem = this._mem;
		return JSON.stringify(json);
	}

}

House.MSG_FACTORYREG.register(XmlAddrMsg);


/** Message de modification d'un Range (sélection), XmlRangeState. */
export class XmlRangeMsg extends XmlAddrMsg implements IXAddrRange {
	static readonly type: string = "xmlRg";

	get start() {return this.addr}

	end: IXAddr | null;

	init(start: IXAddr, end?: IXAddr): this {
		super.init(start);
		this.end = XA.freeze(end);
		return this;
	}

	adjustDeleteX(xa: IXAddr, dLen: number) {
		super.adjustDeleteX(xa, dLen);
		if (this.end) this.end = XA.freeze(XA.translateDelSeq(this.end, xa, -dLen));
	}

	adjustInsertX(xa: IXAddr, len: number, biasBefore?: boolean) {
		super.adjustInsertX(xa, len, biasBefore);
		if (this.end) this.end = XA.freeze(XA.translateInsSeq(this.end, xa, len));
	}

	protected _jumpDeleteX(xa: IXAddr, len: number, memArray: Array<any>): Array<any> {
		//on mémorise les écarts
		const oldStart = this.addr;
		const oldEnd = this.end;
		this.adjustDeleteX(xa, -len);
		memArray = this._pushXaDiff(oldStart, xa, len, this.addr, memArray);
		if (oldEnd) memArray = this._pushXaDiff(oldEnd, xa, len, this.end, memArray);
		return memArray;
	}

	protected _jumpInsertX(xa: IXAddr, len: number, deltaArray: Array<any>) {
		if (this.end) this.end = this._popXaDiff(this.end, xa, len, deltaArray);
		super._jumpInsertX(xa, len, deltaArray);
	}

	invert() {
		//simple clone.
		return new XmlRangeMsg().init(this.addr, this.end).copyStableMetasFrom(this);
	}

	toJson(json: any) {
		super.toJson(json);
		json.end = this.end;
	}

	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromJson(json, factoryReg);
		this.end = XA.freeze(json.end);
	}

	protected cloneFrom(o: this, purpose?: string) {
		super.cloneFrom(o, purpose);
		this.end = o.end;
	}
}

House.MSG_FACTORYREG.register(XmlRangeMsg);


/**
 * Mémorisation d'un chemin entre xaFrom et xaChild.
 * xaFrom est un ancêtre ou un oncle précédent de xaChild.
 *
 * @param content Contenu à stocker qui sera restitué au forward
 */
function pushSubXa(array: Array<any>, xaFrom: IXAddr, xaChild: IXAddr, content: any): Array<any> {
	const lastFrom = xaFrom.length - 1;
	//Le 1er item du subXa est la distance entre xaFrom et xaChild (0 si ancêtre, >0 si oncle précédent.
	const subXa: IXAddr = typeof xaChild[lastFrom] === "string" ? [] : [(xaChild[lastFrom] as number) - (xaFrom[lastFrom] as number)];
	//on copie ensuite le subXa
	for (let i = lastFrom + 1; i < xaChild.length; i++) subXa.push(xaChild[i]);
	if (!array) array = [];
	array.push(content);
	array.push(subXa);
	return array;
}

/**
 * Réapplique un SubXa
 */
function applySubXa(fromXa: IXAddr, subXa: IXAddr): IXAddr {
	const fromLast = XA.last(fromXa);
	const newXa = XA.newBd(fromXa);
	if (typeof fromLast === "string") {
		//contexte attribut.
		newXa.append(subXa[0]);
	} else {
		//le 1er subXa est un décalage.
		newXa.setAtDepth(-1, fromLast + (subXa[0] as number));
	}
	for (let i = 1; i < subXa.length; i++) newXa.append(subXa[i]);
	return newXa.freeze();
}

/**
 * Mémorisation d'un éventuel décalage entre deux XAddr.
 */
function pushDeltaX(array: Array<any>, xa1: IXAddr, xa2: IXAddr): Array<any> {
	let depth;
	let delta = 0;
	for (let dpth = 0, max = Math.min(xa1.length, xa2.length) - 1; dpth <= max; dpth++) {
		const offs1 = xa1[dpth] as number;
		const offs2 = xa2[dpth] as number;
		if (offs1 !== offs2) {
			depth = dpth;
			delta = offs1 - offs2;
			break;
		}
	}
	if (!array) array = [];
	if (delta > 0) array.push(depth);
	array.push(delta);
	return array;
}

type  DeltaXSubXaBuffer = { delta?: number, depth?: number, subXa?: IXAddr, content?: any };
const _popDeltaXSubXaBuffer: DeltaXSubXaBuffer = {};

function popDeltaXOrSubXa(array: Array<any>): DeltaXSubXaBuffer {
	const firstVal = array.pop();
	if (firstVal === 0) {
		_popDeltaXSubXaBuffer.delta = 0;
		_popDeltaXSubXaBuffer.depth = 0;
		_popDeltaXSubXaBuffer.subXa = null;
	} else if (firstVal > 0) {
		_popDeltaXSubXaBuffer.delta = firstVal;
		_popDeltaXSubXaBuffer.depth = array.pop();
		_popDeltaXSubXaBuffer.subXa = null;
	} else {
		_popDeltaXSubXaBuffer.subXa = firstVal;
		_popDeltaXSubXaBuffer.content = array.pop();
	}
	return _popDeltaXSubXaBuffer;
}


/**
 *
 * @param position
 *            'o' : séquences différentes
 *            'b' : même séquence, startBefore
 *            'a' : même séquence, startAfter
 *            'i" : même séquence, insert imbriqué
 * @param startOffset Utile uniquement si position==='i'
 */
function pushPosInsInsX(array: Array<any>, position: string, startOffset?: number): Array<any> {
	if (!array) array = [];
	if (position === 'i') array.push(startOffset);
	array.push(position);
	return array;
}

type  PosInsInsXOrSubXaBuffer = { pos?: string, startOffset?: number, subXa?: IXAddr, content?: any };
const _popPosInsInsXSubXaBuffer: PosInsInsXOrSubXaBuffer = {};

function popPosInsInsXOrSubXa(array: Array<any>): PosInsInsXOrSubXaBuffer {
	const firstVal = array.pop();
	if (Array.isArray(firstVal)) {
		_popPosInsInsXSubXaBuffer.subXa = firstVal;
		_popPosInsInsXSubXaBuffer.content = array.pop();
		_popPosInsInsXSubXaBuffer.pos = null;
	} else {
		_popPosInsInsXSubXaBuffer.pos = firstVal;
		if (firstVal === 'i') _popPosInsInsXSubXaBuffer.startOffset = array.pop();
		_popPosInsInsXSubXaBuffer.subXa = null;
	}
	return _popPosInsInsXSubXaBuffer;
}


function pushPosInsDelX(array: Array<any>, startBefore: boolean, startAfter: boolean, startOffset: number, endBefore: boolean, endAfter: boolean, endOffset: number) {
	if (!array) array = [];
	//if(StringHouse.DEBUG) {
	array.push(startBefore);
	array.push(startAfter);
	array.push(startOffset);
	array.push(endOffset);
	array.push(endBefore);
	array.push(endAfter);
	return array;
	//} else { version moins lisible optimisée ...}
}

type PosInsDelXSubXaBuffer = {
	startBefore?: boolean, startAfter?: boolean, startOffset?: number,
	endBefore?: boolean, endAfter?: boolean, endOffset?: number,
	subXa?: IXAddr, content?: any,
	delta?: number, depth?: number
};
const _popPosInsDelXSubXaBuffer: PosInsDelXSubXaBuffer = {};

function popPosInsDelXOrSubXa(array: Array<any>): PosInsDelXSubXaBuffer {
	//if(StringHouse.DEBUG) {
	const firstVal = array.pop();
	if (Array.isArray(firstVal)) {
		//mémoire d'un pushSubXa
		_popPosInsDelXSubXaBuffer.subXa = firstVal;
		_popPosInsDelXSubXaBuffer.content = array.pop();
	} else if (typeof firstVal === 'number') {
		//mémoire d'un pushDeltaX
		_popPosInsDelXSubXaBuffer.delta = firstVal;
		_popPosInsDelXSubXaBuffer.depth = firstVal > 0 ? array.pop() : 0;
		_popPosInsDelXSubXaBuffer.subXa = null;
	} else {
		//mémoire d'un pushPosInsDelX
		_popPosInsDelXSubXaBuffer.endAfter = firstVal;
		_popPosInsDelXSubXaBuffer.endBefore = array.pop();
		_popPosInsDelXSubXaBuffer.endOffset = array.pop();
		_popPosInsDelXSubXaBuffer.startOffset = array.pop();
		_popPosInsDelXSubXaBuffer.startAfter = array.pop();
		_popPosInsDelXSubXaBuffer.startBefore = array.pop();
		_popPosInsDelXSubXaBuffer.subXa = null;
		_popPosInsDelXSubXaBuffer.delta = null;
	}
	return _popPosInsDelXSubXaBuffer;
	//} else { version moins lisible optimisée ...}
}

/**
 * Construit un msg pour supprimer un IXAddrRange.
 */
export function deleteXmlRange(this: void, root: Node, range: IXAddrRange): MsgOt {
	const seqs = XA.range2Seqs(range, {excludeAtts: true});
	const msgs = [];
	for (let i = seqs.length - 1; i >= 0; i--) {
		const seq = seqs[i];
		let offset = XA.last(seq.start) as number;
		if (offset === -1) {
			//delete le set d'attributs.
			const ctn = XA.findDomContainer(seq.start, root);
			if (ctn instanceof Element) {
				const atts = ctn.attributes;
				for (let ia = 0, s = atts.length; ia < s; ia++) {
					const att = atts.item(ia);
					msgs.push(new XmlStrMsg().init(XA.setAtDepth(seq.start, -1, att.nodeName), null));
				}
			}
			if (seq.len === 0) break;
			seq.start = XA.setAtDepth(seq.start, -1, 0);
			offset = 0;
		}
		if (seq.len === Infinity) {
			const ctn = XA.findDomContainer(seq.start, root);
			switch (ctn.nodeType) {
			case Node.ELEMENT_NODE:
			case Node.DOCUMENT_NODE:
				seq.len = ctn.childNodes.length - offset;
				break;
			case Node.ATTRIBUTE_NODE:
			case Node.TEXT_NODE:
			case Node.COMMENT_NODE:
				seq.len = ctn.nodeValue.length - offset;
				break;
			}
		}
		if (seq.len > 0) msgs.push(new XmlDeleteMsg().init(seq.start, seq.len));
	}
	if (msgs.length > 1) return new XmlListMsgOt().initList(msgs);
	if (msgs.length === 1) return msgs[0];
	return null;
}

/** Remplace tout le contenu d'un élement. */
export function replaceXmlContent(parent: Element, newContent: IJmlSet): IXmlPrgSensitiveMsg {
	const xa = XA.append(XA.from(parent), 0);
	if (parent.firstChild) {
		return new XmlListMsgOt().initList([
			new XmlDeleteMsg().init(xa, parent.childNodes.length),
			new XmlInsertMsg().init(xa, newContent)
		]);
	}
	return new XmlInsertMsg().init(xa, newContent);
}


export function applyXmlMsgOnElt(msg: IXmlMsg, eltDepth: number, elt: Element) {
	switch (msg.type) {
	case XmlInsertMsg.type : {
		const m = msg as XmlInsertMsg;
		if (m.killed) return;
		JML.insertJmlInDom(msg.xa.slice(eltDepth), m.jml, elt, true);
		break;
	}
	case XmlDeleteMsg.type : {
		const m = msg as XmlDeleteMsg;
		if (m.killed) return;
		DOM.deleteSequenceInDom(msg.xa.slice(eltDepth), m.len, elt);
		break;
	}
	case XmlStrMsg.type : {
		const m = msg as XmlStrMsg;
		if (m.killed) return;
		const eltTarget = XA.findDomContainer(msg.xa.slice(eltDepth), elt);
		if (!(eltTarget instanceof Element)) throw Error(`Container xAddr part is not an element: ${m.xa}`);
		const name = XA.last(m.xa);
		if (typeof name === 'number') throw Error(`Last xAddr part is not an attribute: ${m.xa}`);
		const sep = name.indexOf(':');
		const ns = sep > 0 ? DOM.lookupNamespaceURI(eltTarget, name.substring(0, sep)) : null;
		if (m.val == null) {
			if (ns) eltTarget.removeAttributeNS(ns, name.substring(sep + 1));
			else eltTarget.removeAttribute(name);
		} else {
			if (ns) eltTarget.setAttributeNS(ns, name, m.val);
			else eltTarget.setAttribute(name, m.val);
		}
		break;
	}
	default:
		throw Error("Unknown msg: " + msg);
	}
}

export function applyXmlMsgOnString(this: void, msg: IXmlMsg, str: string): string {
	const offset = XA.last(msg.xa);
	if (typeof offset !== 'number') throw Error(`Last member of ${msg.xa} must be a number in ${msg}`);
	switch (msg.type) {
	case XmlInsertMsg.type : {
		const m = msg as XmlInsertMsg;
		if (m.killed) return str;
		if (offset === 0) {
			return m.jml + str;
		} else if (offset === str.length) {
			return str + m.jml;
		}
		return str.substring(0, offset) + m.jml + str.substring(offset);
	}
	case XmlDeleteMsg.type : {
		const m = msg as XmlDeleteMsg;
		if (m.killed) return str;
		return str.substring(0, offset) + str.substring(offset + m.len);
	}
	default:
		throw Error("Unknown msg: " + msg);
	}
}
